import React, { useState, useEffect } from "react";
import CommonModal from "../common11/commonModals/CommonModal";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import { useForm } from "react-hook-form";

// Sample test names – replace with API when available
const TEST_NAME_OPTIONS = [
  { id: 1, label: "CBC - Haematology Profile", value: "cbc" },
  { id: 2, label: "Liver Function Panel", value: "lfp" },
  { id: 3, label: "Thyroid Profile", value: "thyroid" },
  { id: 4, label: "Blood Sugar Fasting", value: "bsf" },
  { id: 5, label: "Lipid Profile", value: "lipid" },
];

// Sample comparison data per test – replace with API when available
const COMMON_DATES = [
  "2025-08-26 12:59:31",
  "2025-08-26 23:06:02",
  "2025-08-28 17:22:17",
  "2025-09-28 10:07:34",
  "2026-01-17 15:39:17",
];

const getTestData = (testValue) => {
  const dataByTest = {
    cbc: {
      parameters: ["Basophil", "Eosinophils", "Hemoglobin", "LYMPHOCYTE", "MCH", "MCHC", "MCV", "Monocyte", "Neutrophils", "Packed cell v...", "Platelet Count", "RBC Count", "RDW-CV", "WBC"],
      values: [
        ["00", "00", "00", "00", "1"], ["2", "1", "2", "3", "2"], ["12.5", "12.8", "13.1", "12.9", "13.2"], ["20", "22", "19", "21", "23"],
        ["28.5", "29.0", "28.8", "29.2", "28.9"], ["32.1", "32.5", "32.0", "32.3", "32.4"], ["88", "86", "90", "87", "89"], ["6", "5", "7", "6", "5"],
        ["72", "70", "74", "71", "70"], ["38.5", "39.0", "38.8", "39.2", "38.9"], ["250", "245", "255", "248", "252"], ["4.5", "4.6", "4.7", "4.55", "4.65"],
        ["12.5", "12.8", "12.6", "12.9", "12.7"], ["8.2", "8.5", "8.8", "8.4", "8.6"],
      ],
    },
    lfp: {
      parameters: ["SGOT", "SGPT", "ALP", "Total Bilirubin", "Direct Bilirubin", "Total Protein", "Albumin", "Globulin"],
      values: [["28", "32", "30", "26", "29"], ["25", "30", "28", "27", "31"], ["85", "90", "88", "92", "87"], ["0.8", "0.9", "0.7", "0.85", "0.95"], ["0.2", "0.25", "0.18", "0.22", "0.28"], ["7.2", "7.5", "7.4", "7.6", "7.3"], ["4.2", "4.4", "4.3", "4.5", "4.1"], ["3.0", "3.1", "3.1", "3.1", "3.2"]],
    },
    thyroid: {
      parameters: ["TSH", "T3", "T4", "Free T3", "Free T4"],
      values: [["2.5", "2.8", "2.6", "2.9", "2.7"], ["1.2", "1.3", "1.25", "1.35", "1.2"], ["8.5", "8.8", "8.6", "9.0", "8.4"], ["3.2", "3.4", "3.3", "3.5", "3.2"], ["1.1", "1.2", "1.15", "1.25", "1.1"]],
    },
    bsf: {
      parameters: ["Fasting Blood Sugar", "PP Blood Sugar", "HbA1c"],
      values: [["98", "102", "96", "100", "99"], ["140", "145", "138", "142", "148"], ["5.8", "6.0", "5.7", "5.9", "5.9"]],
    },
    lipid: {
      parameters: ["Total Cholesterol", "Triglycerides", "HDL", "LDL", "VLDL"],
      values: [["185", "192", "188", "190", "195"], ["120", "125", "118", "122", "128"], ["45", "48", "46", "47", "44"], ["115", "120", "118", "119", "122"], ["24", "25", "23", "24", "26"]],
    },
  };
  const d = dataByTest[testValue] ?? dataByTest.cbc;
  return { ...d, dates: COMMON_DATES };
};

const TestCompareModal = ({ isOpen, onClose, patientInfo = {} }) => {
  const name = patientInfo?.name ?? "—";
  const uhid = patientInfo?.uhid ?? "—";
  const age = patientInfo?.age ?? patientInfo?.ageGender ?? "—";
  const gender = patientInfo?.gender ?? "—";
  const [tableData, setTableData] = useState(null);
  const { control } = useForm({
    defaultValues: { testName: TEST_NAME_OPTIONS[0] },
  });

  useEffect(() => {
    if (!isOpen) {
      setTableData(null);
    } else {
      setTableData(getTestData(TEST_NAME_OPTIONS[0]?.value ?? "cbc"));
    }
  }, [isOpen]);

  const handleTestChange = (option) => {
    if (option?.value) setTableData(getTestData(option.value));
  };

  const handlePrintReport = () => {
    window.print();
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Test Comparison"
      size="xl"
      showCloseButton
      disableBackdropClose={true}
      DialogContent={
        <div className="space-y-4">
          {/* Patient info + Test dropdown – same line */}
          <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                  {name && name !== "—" ? (
                    <span className="text-lg font-semibold text-gray-600">
                      {String(name).charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-lg font-semibold text-gray-600">?</span>
                  )}
                </div>
              </div>
              <div className="flex flex-1 flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm min-w-0">
                <span className="text-[#217E90] font-semibold truncate">{name}</span>
                <div className="flex items-baseline gap-3">
                  <span className="text-gray-500 text-xs shrink-0">Age</span>
                  <span className="text-[#217E90] font-semibold">: {age}</span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-gray-500 text-xs shrink-0">Gender</span>
                  <span className="text-[#217E90] font-semibold">: {gender}</span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-gray-500 text-xs shrink-0">UHID</span>
                  <span className="text-[#217E90] font-semibold">: {uhid}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center flex-shrink-0">
              <div className="w-[200px] sm:w-[240px]">
                <SearchDropdown
                  name="testName"
                  control={control}
                  label="Test Name"
                  dataArray={TEST_NAME_OPTIONS}
                  isSearchable
                  isClearable={false}
                  isFloatingLabel
                  fontSize="text-xs"
                  selectSize="h-8"
                  placeholder="Select test..."
                  onChange={handleTestChange}
                />
              </div>
            </div>
          </div>

          {/* Comparison table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="border-b border-r px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                      Parameter Name
                    </th>
                    {tableData?.dates?.map((d) => (
                      <th
                        key={d}
                        className="border-b border-r px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap"
                      >
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData?.parameters?.map((param, i) => (
                    <tr
                      key={param}
                      className={i % 2 === 1 ? "bg-yellow-50/50" : "bg-white"}
                    >
                      <td className="border-b border-r px-3 py-2 font-medium text-gray-800">
                        {param}
                      </td>
                      {tableData.values[i]?.map((val, j) => (
                        <td key={j} className="border-b border-r px-3 py-2 text-gray-700">
                          {val}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!tableData && (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  No comparison data available
                </div>
              )}
            </div>
          </div>
        </div>
      }
      actions={[
        {
          onClick: handlePrintReport,
          label: "Print Report",
          variant: "danger-outline",
          className: "border-red-600 text-red-600 hover:bg-red-50",
        },
      ]}
    />
  );
};

export default TestCompareModal;
